/**
 * SecondBrain - PARA Method Knowledge Organization
 *
 * Organize knowledge into:
 * - Projects: Active goals with deadlines
 * - Areas: Ongoing responsibilities
 * - Resources: Reference materials
 * - Archives: Completed/inactive items
 */

import { useState, useEffect, useCallback } from "react";
import { Card, Spinner, Alert, Button, Form, Badge, Pagination } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import AddParaItemModal from "./common/AddParaItemModal";
import ConfirmationPopup from "./common/ConfirmationPopup";
import "../assets/css/knowledge-base.css";

const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || "http://localhost:3000";
const PAGE_SIZE = 10;

const CATEGORIES = [
  { id: "all", label: "All", icon: "bi-grid-fill" },
  { id: "project", label: "Projects", icon: "bi-rocket-takeoff-fill" },
  { id: "area", label: "Areas", icon: "bi-bullseye" },
  { id: "resource", label: "Resources", icon: "bi-book-fill" },
  { id: "archive", label: "Archives", icon: "bi-archive-fill" },
];

const PRIORITY_COLORS = {
  high: "danger",
  medium: "warning",
  low: "secondary",
};

// Helper: Truncate text to max length
const truncateText = (text, maxLength = 150) => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
};

// Helper: Strip markdown symbols for clean display
const stripMarkdown = (text) => {
  if (!text) return "";
  return text
    .replace(/#{1,6}\s?/g, "")           // Remove headers
    .replace(/\*\*([^*]+)\*\*/g, "$1")   // Bold to plain
    .replace(/\*([^*]+)\*/g, "$1")       // Italic to plain
    .replace(/`{1,3}[^`]*`{1,3}/g, "")   // Remove code blocks
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Links to text
    .replace(/---+/g, "")                // Remove horizontal rules
    .replace(/\n{2,}/g, " ")             // Multiple newlines to space
    .replace(/\n/g, " ")                 // Newlines to space
    .trim();
};

export default function SecondBrain({ userId }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Multi-select state
  const [selectedItems, setSelectedItems] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  // Expanded items (show full verbatim content)
  const [expandedItems, setExpandedItems] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Load items
  const loadItems = useCallback(async (page = 1) => {
    if (!userId) return;

    try {
      setLoading(true);
      const categoryParam = activeCategory !== "all" ? `&category=${activeCategory}` : "";
      const searchParam = searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : "";
      const response = await fetch(
        `${API_URL}/api/second-brain?user_id=${userId}&page=${page}&page_size=${PAGE_SIZE}${categoryParam}${searchParam}`
      );
      const data = await response.json();

      if (data.items) {
        setItems(data.items);
        if (data.pagination) {
          setCurrentPage(data.pagination.page);
          setTotalPages(data.pagination.total_pages);
          setTotalItems(data.pagination.total);
        }
      } else if (data.error) {
        setError(data.error);
      }
    } catch (err) {
      console.error("[SecondBrain] Error loading:", err);
      setError("Failed to load items");
    } finally {
      setLoading(false);
    }
  }, [userId, activeCategory, searchQuery]);

  // Reload when category or userId changes
  useEffect(() => {
    if (!userId) return;
    
    const fetchItems = async () => {
      try {
        setLoading(true);
        const categoryParam = activeCategory !== "all" ? `&category=${activeCategory}` : "";
        const response = await fetch(
          `${API_URL}/api/second-brain?user_id=${userId}&page=1&page_size=${PAGE_SIZE}${categoryParam}`
        );
        const data = await response.json();

        if (data.items) {
          setItems(data.items);
          setCurrentPage(1);
          if (data.pagination) {
            setTotalPages(data.pagination.total_pages);
            setTotalItems(data.pagination.total);
          }
        }
      } catch (err) {
        console.error("[SecondBrain] Error loading:", err);
        setError("Failed to load items");
      } finally {
        setLoading(false);
      }
    };
    
    fetchItems();
  }, [userId, activeCategory]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadItems(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadItems(page);
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setCurrentPage(1);
  };

  // Handle add/edit item
  const handleSaveItem = async (itemData) => {
    try {
      const action = itemToEdit ? "update" : "add";
      const payload = {
        action,
        user_id: userId,
        ...(itemToEdit ? { item_id: itemToEdit.id } : {}),
        ...itemData,
      };

      const response = await fetch(`${API_URL}/api/second-brain`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success || data.item) {
        setShowAddModal(false);
        setItemToEdit(null);
        loadItems(itemToEdit ? currentPage : 1);
      } else {
        setError(data.error || "Failed to save item");
      }
    } catch (err) {
      console.error("[SecondBrain] Error saving:", err);
      setError("Failed to save item");
    }
  };

  // Handle archive
  const handleArchive = async (item) => {
    try {
      const response = await fetch(`${API_URL}/api/second-brain`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: item.category === "archive" ? "unarchive" : "archive",
          user_id: userId,
          item_id: item.id,
          restore_category: "resource",
        }),
      });

      const data = await response.json();
      if (data.success) {
        loadItems(currentPage);
      } else {
        setError(data.error || "Failed to archive item");
      }
    } catch (err) {
      console.error("[SecondBrain] Error archiving:", err);
      setError("Failed to archive item");
    }
  };

  // Handle delete
  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    // Bulk delete
    if (selectedItems.length > 0) {
      try {
        setDeleting(true);
        const response = await fetch(`${API_URL}/api/second-brain`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "bulk_delete",
            user_id: userId,
            item_ids: selectedItems,
          }),
        });

        const data = await response.json();
        if (data.success) {
          setShowDeleteConfirm(false);
          setSelectedItems([]);
          setIsMultiSelectMode(false);
          // If we deleted all items on this page, go to previous page
          if (selectedItems.length >= items.length && currentPage > 1) {
            loadItems(currentPage - 1);
          } else {
            loadItems(currentPage);
          }
        } else {
          setError(data.error || "Failed to delete items");
        }
      } catch (err) {
        console.error("[SecondBrain] Error bulk deleting:", err);
        setError("Failed to delete items");
      } finally {
        setDeleting(false);
      }
      return;
    }

    // Single delete
    if (!itemToDelete) return;

    try {
      setDeleting(true);
      const response = await fetch(`${API_URL}/api/second-brain`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "delete",
          user_id: userId,
          item_id: itemToDelete.id,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setShowDeleteConfirm(false);
        setItemToDelete(null);
        // If we deleted the last item on a page, go to previous page
        if (items.length === 1 && currentPage > 1) {
          loadItems(currentPage - 1);
        } else {
          loadItems(currentPage);
        }
      } else {
        setError(data.error || "Failed to delete item");
      }
    } catch (err) {
      console.error("[SecondBrain] Error deleting:", err);
      setError("Failed to delete item");
    } finally {
      setDeleting(false);
    }
  };

  // Multi-select handlers
  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    if (isMultiSelectMode) {
      setSelectedItems([]);
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map((item) => item.id));
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedItems.length === 0) return;
    setItemToDelete(null);
    setShowDeleteConfirm(true);
  };

  // Toggle verbatim/summary view
  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Handle edit
  const handleEditClick = (item) => {
    setItemToEdit(item);
    setShowAddModal(true);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get category icon
  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find((c) => c.id === category);
    return cat?.icon || "bi-folder";
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const paginationItems = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    paginationItems.push(
      <Pagination.First
        key="first"
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
      />
    );
    paginationItems.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
    );

    if (startPage > 1) {
      paginationItems.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let page = startPage; page <= endPage; page++) {
      paginationItems.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    if (endPage < totalPages) {
      paginationItems.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }

    paginationItems.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
    );
    paginationItems.push(
      <Pagination.Last
        key="last"
        onClick={() => handlePageChange(totalPages)}
        disabled={currentPage === totalPages}
      />
    );

    return (
      <div className="kb-pagination-wrapper">
        <span className="kb-pagination-info">
          Showing {((currentPage - 1) * PAGE_SIZE) + 1}-{Math.min(currentPage * PAGE_SIZE, totalItems)} of {totalItems}
        </span>
        <Pagination className="mb-0">{paginationItems}</Pagination>
      </div>
    );
  };

  // Get count by category
  const getCategoryCount = (category) => {
    if (category === "all") return totalItems;
    return items.filter(item => item.category === category).length;
  };

  if (loading && items.length === 0) {
    return (
      <div className="knowledge-base-container">
        <div className="d-flex justify-content-center align-items-center" style={{ height: "300px" }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="knowledge-base-container">
      {/* Stats Bar */}
      <div className="kb-stats-bar">
        <div className="kb-stat-card">
          <div className="kb-stat-icon teal">
            <i className="bi bi-journal-bookmark-fill"></i>
          </div>
          <div className="kb-stat-info">
            <h3>{totalItems}</h3>
            <p>Total Items</p>
          </div>
        </div>
        <div className="kb-stat-card">
          <div className="kb-stat-icon orange">
            <i className="bi bi-rocket-takeoff-fill"></i>
          </div>
          <div className="kb-stat-info">
            <h3>{items.filter(item => item.category === "project").length}</h3>
            <p>Active Projects</p>
          </div>
        </div>
      </div>

      {/* Search & Actions Bar */}
      <div className="kb-actions-bar">
        <div className="kb-search-wrapper">
          <i className="bi bi-search kb-search-icon-left"></i>
          <Form onSubmit={handleSearch} style={{ display: 'contents' }}>
            <input
              type="text"
              placeholder="Search your second brain..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Form>
        </div>
        <div className="kb-action-buttons">
          {isMultiSelectMode ? (
            <>
              <button
                className="kb-select-all-btn"
                onClick={toggleSelectAll}
              >
                <i className={`bi ${selectedItems.length === items.length && items.length > 0 ? 'bi-check-square-fill' : 'bi-square'}`}></i>
                {selectedItems.length === items.length && items.length > 0 ? 'Deselect All' : 'Select All'}
              </button>
              <button
                className="kb-delete-selected-btn"
                onClick={handleBulkDeleteClick}
                disabled={selectedItems.length === 0}
              >
                <i className="bi bi-trash"></i>
                Delete ({selectedItems.length})
              </button>
              <button
                className="kb-cancel-btn"
                onClick={toggleMultiSelectMode}
              >
                <i className="bi bi-x-lg"></i>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                className="kb-select-btn"
                onClick={toggleMultiSelectMode}
                disabled={items.length === 0}
              >
                <i className="bi bi-check2-square"></i>
                Select
              </button>
              <button
                className="kb-add-btn"
                onClick={() => {
                  setItemToEdit(null);
                  setShowAddModal(true);
                }}
              >
                <i className="bi bi-plus-lg"></i>
                Add Item
              </button>
            </>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="sb-tabs-container">
        <div className="sb-category-tabs">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              className={`sb-tab ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => handleCategoryChange(cat.id)}
            >
              <i className={`bi ${cat.icon} me-2`}></i>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {/* Items List */}
      <div className="kb-memories-list">
        {items.length === 0 ? (
          <div className="kb-empty-state">
            <div className="kb-empty-icon">
              <i className="bi bi-journal-bookmark"></i>
            </div>
            <h5>No items yet</h5>
            <p>
              {searchQuery
                ? "No items match your search"
                : `Start organizing your knowledge with the PARA method.`}
            </p>
            {!searchQuery && (
              <Button
                variant="outline-primary"
                onClick={() => setShowAddModal(true)}
              >
                <i className="bi bi-plus-lg me-2"></i>
                Add Your First Item
              </Button>
            )}
          </div>
        ) : (
          items.map((item) => (
            <Card
              key={item.id}
              className={`kb-memory-card sb-item-card ${isMultiSelectMode && selectedItems.includes(item.id) ? 'selected' : ''}`}
              onClick={isMultiSelectMode ? () => toggleItemSelection(item.id) : undefined}
              style={isMultiSelectMode ? { cursor: 'pointer' } : {}}
            >
              <Card.Body>
                <div className="sb-item-content">
                  {isMultiSelectMode && (
                    <div className="kb-checkbox-wrapper">
                      <Form.Check
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => toggleItemSelection(item.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="kb-item-checkbox"
                      />
                    </div>
                  )}
                  <div className={`sb-item-icon ${item.category}`}>
                    <i className={`bi ${getCategoryIcon(item.category)}`}></i>
                  </div>
                  <div className="sb-item-main">
                    <div className="sb-item-header">
                      <h6 className="sb-item-title">{item.title}</h6>
                      <div className="sb-item-badges">
                        {item.priority && (
                          <Badge bg={PRIORITY_COLORS[item.priority] || "secondary"}>
                            {item.priority}
                          </Badge>
                        )}
                        {item.tags?.slice(0, 3).map((tag, idx) => (
                          <Badge key={idx} bg="info" className="sb-tag">
                            {tag}
                          </Badge>
                        ))}
                        {item.tags?.length > 3 && (
                          <Badge bg="secondary" className="sb-tag-more">
                            +{item.tags.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>
                    {(item.content || item.verbatim) && (
                      <>
                        {expandedItems[item.id] ? (
                          <div className="sb-item-description expanded sb-markdown-content">
                            <ReactMarkdown>{item.verbatim || item.content}</ReactMarkdown>
                          </div>
                        ) : (
                          <p className="sb-item-description">
                            {truncateText(stripMarkdown(item.summary || item.content), 150)}
                          </p>
                        )}
                        {((item.content?.length > 150) || item.verbatim) && (
                          <button 
                            className="sb-toggle-verbatim"
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(item.id); }}
                          >
                            <i className={`bi ${expandedItems[item.id] ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}></i>
                            {expandedItems[item.id] ? 'Show Less' : 'Read More'}
                          </button>
                        )}
                      </>
                    )}
                    {item.connections && item.connections.length > 0 && (
                      <div className="sb-connections-badge">
                        <i className="bi bi-link-45deg me-1"></i>
                        {item.connections.length} related item(s)
                      </div>
                    )}
                    {item.attachments && item.attachments.length > 0 && (
                      <div className="sb-attachments-section">
                        {item.attachments.map((att, idx) => (
                          <a 
                            key={idx}
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sb-attachment-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className={`bi ${
                              att.type === 'image' ? 'bi-image' : 
                              att.type === 'pdf' ? 'bi-file-pdf' : 
                              att.type === 'url' ? 'bi-link-45deg' :
                              'bi-paperclip'
                            } me-1`}></i>
                            {att.type === 'image' ? 'View Image' : 
                             att.type === 'pdf' ? 'View PDF' : 
                             att.type === 'url' ? 'Open Link' :
                             'View Attachment'}
                          </a>
                        ))}
                      </div>
                    )}
                    <div className="kb-memory-meta">
                      <span className="kb-memory-date">
                        <i className="bi bi-calendar3 me-1"></i>
                        {formatDate(item.created_at)}
                      </span>
                      {item.due_date && (
                        <span className="sb-due-date">
                          <i className="bi bi-clock me-1"></i>
                          Due: {formatDate(item.due_date)}
                        </span>
                      )}
                      <span className="sb-category-badge">
                        <i className={`bi ${getCategoryIcon(item.category)} me-1`}></i>
                        {item.category}
                      </span>
                    </div>
                  </div>
                  {!isMultiSelectMode && (
                    <div className="sb-item-actions">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditClick(item)}
                        title="Edit"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteClick(item)}
                        title="Delete"
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {renderPagination()}

      {/* Loading overlay */}
      {loading && items.length > 0 && (
        <div className="kb-loading-overlay">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Updating...</span>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AddParaItemModal
        show={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setItemToEdit(null);
        }}
        onSave={handleSaveItem}
        editItem={itemToEdit}
      />

      {/* Delete Confirmation */}
      <ConfirmationPopup
        show={showDeleteConfirm}
        handleClose={() => {
          setShowDeleteConfirm(false);
          setItemToDelete(null);
        }}
        handleConfirm={handleDelete}
        title={selectedItems.length > 0 ? `Delete ${selectedItems.length} Items?` : "Delete Item?"}
        body={
          selectedItems.length > 0
            ? `Are you sure you want to delete ${selectedItems.length} selected item${selectedItems.length > 1 ? 's' : ''}? This action cannot be undone.`
            : itemToDelete
              ? `Are you sure you want to delete "${itemToDelete.title}"?`
              : "Are you sure you want to delete this item?"
        }
        confirmText={deleting ? "Deleting..." : "Delete"}
        confirmVariant="danger"
      />
    </div>
  );
}
